import React, { type ReactNode, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { deburr, lowerCase } from 'lodash-es'

import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser.ts'
import { type AppViewProps } from '@/components/templates/AppView'
import { useAdminBreakers } from '@/model/contexts/useAdminBreakers.ts'
import { useNotificationSoundControl } from '@/model/contexts/useNotificationSoundControl.ts'
import { useRegion } from '@/model/contexts/useRegion.ts'
import { useTeams } from '@/model/contexts/useTeams.ts'
import { useTransportRequests } from '@/model/contexts/useTransportRequests.ts'
import { useMapData } from '@/pages/dashboard/data/useMapData.ts'

import { useTeamSidePanelData } from './data/useTeamSidePanelData.ts'
import { useTransportRequestCardList } from './data/useTransportRequestCardList.ts'
import { AppPageStateContext } from './AppPageStateProvider.tsx'

type AppPageDataContextValue = Pick<AppViewProps, 'data' | 'onHighlight'>

export const AppPageDataContext = React.createContext<AppPageDataContextValue>({
  data: {
    requestList: {},
    header: {
      region: { name: '', messageText: '', messageTitle: '', statuses: [] },
      waitTime: { delay: '' },
      assignation: { state: 'manual', disabled: true },
      mobileRequest: {},
      notificationSoundEnabled: false,
    },
    sidepanel: {},
    map: {},
  },
  onHighlight() {},
})

export interface AppPageDataProviderProps {
  children: ReactNode
}

export const AppPageDataProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const {
    state: {
      selectedBase,
      selectedStartingLocation,
      requestSortBy,
      completedRequestSortBy,
      teamSortBy,
      searchQuery,
      television,
      pending,
    },
    onHighlightInternal,
  } = useContext(AppPageStateContext)
  const intl = useIntl()

  const { canToggleWaitTime, canToggleMobileApp } = useAuthenticatedFirebaseUser()
  const { region } = useRegion()
  const { transportRequests } = useTransportRequests()
  const { teams } = useTeams()
  const { regionState, automatedAssignments, mobileAppRequests } = useAdminBreakers()
  const { soundEnabled } = useNotificationSoundControl()

  const { filteredTeams, filteredTransportRequests } = useMemo(() => {
    const selectedTeams = teams.map((team) => ({
      ...team,
      hidden:
        (selectedBase ? selectedBase !== 'all' && selectedBase !== team.base : false) ||
        (selectedStartingLocation
          ? selectedStartingLocation !== 'all' && selectedStartingLocation !== team.startingLocation
          : false),
    }))
    const queries = lowerCase(deburr(searchQuery)).split(/\s+/)
    const selectedTransportRequests = transportRequests.map((request) => ({
      ...request,
      hidden:
        (request.team && selectedTeams.find((team) => team._id === request.team?._id)?.hidden) ||
        !queries.some(
          (query) =>
            String(request.piloteId).includes(query) ||
            request.customerPhoneNumber.toLowerCase().includes(query) ||
            lowerCase(deburr(request.customerFirstName)).includes(query) ||
            lowerCase(deburr(request.customerLastName)).includes(query) ||
            lowerCase(deburr(request.origin.formattedAddress)).includes(query) ||
            lowerCase(deburr(request.destination.formattedAddress)).includes(query)
        ),
    }))
    return {
      filteredTeams: selectedTeams,
      filteredTransportRequests: selectedTransportRequests,
    }
  }, [searchQuery, selectedBase, selectedStartingLocation, teams, transportRequests])

  const requestCardList = useTransportRequestCardList({
    transportRequests: filteredTransportRequests,
    teams,
    requestSortBy,
    completedRequestSortBy,
    baseUrl: `${import.meta.env.VITE_PILOTE_BASE_URL}/app/transport`,
  })
  const selectedPendingTransportRequest = pending?.selected
    ? transportRequests.find(({ _id }) => _id === pending.selected)
    : null
  const unreadConversationCount = teams.filter((t) => t.messagingStatus.hasUnreadTeamMessages).length
  const sidePanelTeams = useTeamSidePanelData({
    teams: filteredTeams,
    teamSortBy,
    onlyShowTeamsThatCanDriveAManualCar: selectedPendingTransportRequest?.customerVehicle?.manualTransmission ?? false,
  })
  const mapDataOptions = television
    ? { transportRequests, teams: teams.filter((t) => t.status !== 'DISSOLVED') }
    : { transportRequests: filteredTransportRequests, teams: filteredTeams }
  const mapData = useMapData(mapDataOptions)

  const value = useMemo<AppPageDataContextValue>(() => {
    const delay = !region.waitTime
      ? intl.formatMessage({ id: 'header.waitTime.noValue' })
      : intl.formatMessage(
          { id: 'header.waitTime.value' },
          {
            min: region.waitTime.minimumInMinutes,
            max: region.waitTime.maximumInMinutes,
          }
        )

    return {
      data: {
        ...mapData,
        requestList: requestCardList,
        header: {
          region: {
            name: region.name,
            state: regionState.state,
            disabled: !region.currentOperatingDate,
            messageTitle: regionState.messageTitle,
            messageText: regionState.messageText,
            statuses: regionState.statuses,
          },
          waitTime: {
            delay,
            state:
              region.waitTimeConfiguration.type === 'increased'
                ? 'addMore'
                : region.waitTime?.exceedsThreshold
                  ? 'exceedsThreshold'
                  : 'default',
            disabled: !region.currentOperatingDate || !canToggleWaitTime,
          },
          assignation: automatedAssignments.disabled
            ? { state: 'manual', disabled: true }
            : !region.isAutomatedAssignmentEnabled
              ? { state: 'manual', disabled: true }
              : { state: region.assignment, disabled: false },
          mobileRequest: {
            state: mobileAppRequests.disabled
              ? 'adminPause'
              : !region.openForTransportRequests
                ? 'pause'
                : region.waitTime?.exceedsThreshold
                  ? 'exceedsThreshold'
                  : 'open',
            disabled: mobileAppRequests.disabled || !region.mobileTransportRequestsEnabled || !canToggleMobileApp,
          },
          notificationSoundEnabled: soundEnabled,
        },
        sidepanel: {
          teams: sidePanelTeams,
          messages:
            teams.length > 0
              ? { regionId: region._id, unreadConversationCount, hasUnreadMessages: unreadConversationCount > 0 }
              : undefined,
        },
      },
      onHighlight: (id) => {
        const transportRequest = transportRequests.find(({ _id }) => _id === id)
        if (transportRequest) {
          onHighlightInternal(transportRequest)
        }
      },
    }
  }, [
    intl,
    region._id,
    region.assignment,
    region.waitTime,
    region.name,
    region.currentOperatingDate,
    region.waitTimeConfiguration.type,
    region.isAutomatedAssignmentEnabled,
    region.openForTransportRequests,
    region.mobileTransportRequestsEnabled,
    mapData,
    requestCardList,
    regionState.state,
    regionState.messageTitle,
    regionState.messageText,
    regionState.statuses,
    canToggleWaitTime,
    automatedAssignments.disabled,
    mobileAppRequests.disabled,
    canToggleMobileApp,
    sidePanelTeams,
    unreadConversationCount,
    transportRequests,
    teams,
    onHighlightInternal,
    soundEnabled,
  ])

  return <AppPageDataContext.Provider value={value}>{children}</AppPageDataContext.Provider>
}
